/**
 * Phase 6 — Redesigned Card
 *
 * Border-only, no shadow. Optional inner-highlight (the "better than Linear" touch).
 */

import * as React from "react";
import { cn } from "@/lib/utils";

interface RdCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Use surface-2 (slightly elevated) — useful inside another card */
  elevated?: boolean;
  /** Hide the inner-highlight (set true for very small cards where it looks weird) */
  flat?: boolean;
  /** Make it interactive — adds hover state */
  interactive?: boolean;
}

export const RdCard = React.forwardRef<HTMLDivElement, RdCardProps>(
  ({ className, elevated, flat, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-[10px] border",
        elevated
          ? "bg-[hsl(var(--rd-surface-2))] border-[hsl(var(--rd-border))]"
          : "bg-[hsl(var(--rd-surface))] border-[hsl(var(--rd-border))]",
        !flat && "shadow-[inset_0_1px_0_0_rgb(255_255_255/0.04)]",
        interactive &&
          "transition-colors duration-[120ms] hover:border-[hsl(var(--rd-border-strong))] cursor-pointer",
        className
      )}
      {...props}
    />
  )
);
RdCard.displayName = "RdCard";

export const RdCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "px-4 py-3 border-b border-[hsl(var(--rd-border-soft))]",
      "flex items-center justify-between gap-3",
      className
    )}
    {...props}
  />
));
RdCardHeader.displayName = "RdCardHeader";

export const RdCardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-[13px] font-semibold tracking-tight text-[hsl(var(--rd-text))]",
      className
    )}
    {...props}
  />
));
RdCardTitle.displayName = "RdCardTitle";

export const RdCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      "text-[12px] text-[hsl(var(--rd-text-muted))] leading-snug",
      className
    )}
    {...props}
  />
));
RdCardDescription.displayName = "RdCardDescription";

export const RdCardBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-4 py-4", className)} {...props} />
));
RdCardBody.displayName = "RdCardBody";

export const RdCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "px-4 py-3 border-t border-[hsl(var(--rd-border-soft))]",
      "flex items-center justify-end gap-2",
      className
    )}
    {...props}
  />
));
RdCardFooter.displayName = "RdCardFooter";
